import { useTranslation } from "react-i18next";

/**
 * The landing-page signature: a clock face drawn from the logo's own
 * geometry — four-point sparkles as the hour ticks, the wordmark's arc
 * as a slowly orbiting sweep, and the real working hours in the middle.
 * Orbit animation lives in .tc-orbit (index.css) and is disabled under
 * prefers-reduced-motion.
 */

// Four-point sparkle, unit-sized (scaled per use) — same shape as the logo's.
const SPARKLE =
  "M0 -1 C0.16 -0.38 0.38 -0.16 1 0 C0.38 0.16 0.16 0.38 0 1 C-0.16 0.38 -0.38 0.16 -1 0 C-0.38 -0.16 -0.16 -0.38 0 -1 Z";

function Sparkle({
  x,
  y,
  size,
  className,
}: {
  x: number;
  y: number;
  size: number;
  className?: string;
}) {
  return (
    <path
      d={SPARKLE}
      transform={`translate(${x} ${y}) scale(${size})`}
      className={className}
    />
  );
}

const C = 240; // dial center
const TICK_R = 205; // sparkle ticks radius
const SWEEP_R = 162; // orbit arc radius

export function ShineDial() {
  const { t } = useTranslation();

  const ticks = Array.from({ length: 12 }, (_, i) => {
    const angle = ((i * 30 - 90) * Math.PI) / 180;
    return {
      x: C + TICK_R * Math.cos(angle),
      y: C + TICK_R * Math.sin(angle),
      major: i % 3 === 0,
    };
  });

  // ~250° of arc out of the full sweep circumference, round-capped like the logo swoosh
  const sweepCircumference = 2 * Math.PI * SWEEP_R;
  const arcLength = sweepCircumference * 0.7;

  return (
    <div className="relative w-full max-w-[420px] mx-auto select-none" aria-hidden="true">
      <svg viewBox="0 0 480 480" className="w-full h-auto block">
        {/* Quiet outer ring the ticks sit on */}
        <circle cx={C} cy={C} r={TICK_R} className="fill-none stroke-border" strokeWidth="1" />

        {/* Sparkle hour ticks — 12/3/6/9 speak louder */}
        {ticks.map((tick, i) => (
          <Sparkle
            key={i}
            x={tick.x}
            y={tick.y}
            size={tick.major ? 13 : 6}
            className={tick.major ? "fill-secondary" : "fill-muted-foreground/40"}
          />
        ))}

        {/* The orbiting sweep — the logo's arc as a minute hand */}
        <g className="tc-orbit">
          <circle
            cx={C}
            cy={C}
            r={SWEEP_R}
            className="fill-none stroke-primary"
            strokeWidth="9"
            strokeLinecap="round"
            strokeDasharray={`${arcLength} ${sweepCircumference - arcLength}`}
          />
          {/* Sparkle riding the tip of the arc (dash starts at 3 o'clock) */}
          <Sparkle x={C + SWEEP_R} y={C} size={22} className="fill-primary" />
          <Sparkle x={C + SWEEP_R} y={C} size={9} className="fill-background" />
          {/* Small trailing companion, like the logo's second sparkle */}
          <Sparkle x={C - SWEEP_R} y={C - 24} size={8} className="fill-secondary" />
        </g>

        {/* Face */}
        <circle
          cx={C}
          cy={C}
          r={132}
          className="fill-card stroke-border"
          strokeWidth="1.5"
        />

        {/* Center content lives inside the SVG (foreignObject) so the
            text scales with the dial and can never spill out of the face */}
        <foreignObject x={C - 124} y={C - 124} width={248} height={248}>
          <div className="w-full h-full flex flex-col items-center justify-center text-center gap-[10px] pointer-events-none">
            <svg viewBox="-1.2 -1.2 2.4 2.4" style={{ width: 20, height: 20 }}>
              <path d={SPARKLE} className="fill-primary" />
            </svg>
            <p
              className="font-display font-bold leading-tight text-foreground m-0"
              style={{ fontSize: 21 }}
            >
              {t("hero.tagline")}
            </p>
            <p
              className="u-eyebrow text-muted-foreground border border-border rounded-full normal-case tracking-normal whitespace-nowrap m-0"
              style={{ fontSize: 11, padding: "5px 12px" }}
            >
              {t("hero.hours")}
            </p>
          </div>
        </foreignObject>
      </svg>
    </div>
  );
}
